import { SxProps, Theme } from '@mui/material';
import { useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { TemplateSkeleton } from '../template.skeleton';
import { TemplateIframeWrapper } from './template-editor.styles';

export interface TemplateEditorProps {
  url: string | null | undefined;
  onReady?: () => void;
  onTemplateDataReceived?: (data: any) => void;
  onError?: (status: any, message: any) => void;
  onChange?: (isChanged: boolean) => void;
  sx?: SxProps<Theme> | undefined;
}

export interface TemplateEditorRefProps {
  getTemplateData: () => void;
}

export const TemplateEditor = forwardRef<TemplateEditorRefProps, TemplateEditorProps>((props, ref) => {
  const { url, onTemplateDataReceived, sx } = props;
  const iframeId = 'TemplateEditor';
  const { getAccessToken } = useOidcWithAdmin();
  const [loading, setLoading] = useState<boolean>(true);
  const [wereFocused, _setWereFocused] = useState<boolean>(false);
  const wereFocusedRef = React.useRef(wereFocused);
  const setWereFocused = (data: boolean) => {
    wereFocusedRef.current = data;
    _setWereFocused(data);
  };

  useEffect(() => {
    if (url) {
      window.addEventListener('message', onIframeMessageReceived);
      window.addEventListener('blur', handleWindowBlur, true);

      return () => {
        window.removeEventListener('message', onIframeMessageReceived);
        window.removeEventListener('blur', handleWindowBlur);
      };
    }
  }, [url]);

  // for CORS security reason hooks and refs dont work with iframe content
  const getIframe = (): HTMLIFrameElement => {
    return document.getElementById(iframeId) as HTMLIFrameElement;
  };

  const postMesageToIframe = (name: string, payload?: any) => {
    const iframe = getIframe();
    iframe.contentWindow!.postMessage({ name: name, payload: payload }, '*');
  };

  const getTemplateData = () => {
    postMesageToIframe('Templating.GetDataRequest');
  };

  useImperativeHandle(ref, () => ({ getTemplateData }));

  const onIframeMessageReceived = (msg: any) => {
    switch (msg.data.name) {
      case 'Templating.Ready':
        onTemplateReady();
        break;
      case 'Templating.Error':
        onTemplateError(msg.data.payload.status, msg.data.payload.message);
        break;
      case 'Templating.Change':
        onTemplateChange();
        break;
      case 'Templating.StatusIsReady':
        if (!wereFocusedRef.current) props.onChange && props.onChange(msg.data.payload.status);
        break;
      case 'Templating.GetDataResponse':
        onTemplateDataReceived && onTemplateDataReceived(JSON.parse(msg.data.payload.data));
        break;
      default:
        break;
    }
  };

  const onIframeLoad = async () => {
    const accessToken = await getAccessToken();
    postMesageToIframe('Templating.Editable', { accessToken: accessToken });
  };

  const onTemplateReady = () => {
    setLoading(false);
    props.onReady && props.onReady();
  };

  const onTemplateError = (status: string, message: string) => {
    props.onError && props.onError(status, message);
  };

  const onTemplateChange = () => {
    props.onChange && props.onChange(true);
  };

  let iframeClickedLast = false;
  const handleWindowBlur = (e: any) => {
    if (document.activeElement!.tagName.toLowerCase() === 'iframe') {
      iframeClickedLast = true;
      setWereFocused(true);
      props.onChange && props.onChange(true);
    }
  };

  return (
    <TemplateIframeWrapper sx={sx}>
      {loading && <TemplateSkeleton />}
      {url && <iframe id={iframeId} src={url} onLoad={onIframeLoad} height={loading ? '0px' : '650px'} />}
    </TemplateIframeWrapper>
  );
});
